'use client';

import {
    createContext,
    useContext,
    ReactNode,
    useState,
    useEffect,
} from 'react';

/**
 * 错误信息接口
 */
export interface ErrorInfo {
    code?: string;
    message?: string;
    permissionCode?: string;
}

/**
 * 错误上下文接口
 */
export interface ErrorContextType {
    errorInfo: ErrorInfo;
    setErrorInfo: (info: ErrorInfo) => void;
    clearErrorInfo: () => void;
}

/**
 * 创建错误上下文
 */
const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

/**
 * 错误上下文钩子
 */
export const useError = (): ErrorContextType => {
    const context = useContext(ErrorContext);
    if (!context) {
        throw new Error('useError must be used within an ErrorProvider');
    }
    return context;
};

/**
 * 从cookie中读取指定名称的值
 */
const getCookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null;

    const cookieMatch = document.cookie.match(
        `(^|;)\\s*${name}\\s*=\\s*([^;]+)`,
    );
    return cookieMatch ? decodeURIComponent(cookieMatch[2]) : null;
};

/**
 * 删除指定名称的cookie
 */
const deleteCookie = (name: string): void => {
    if (typeof document === 'undefined') return;

    document.cookie = `${name}=; Max-Age=0; path=/;`;
};

/**
 * 错误上下文提供者组件
 */
export const ErrorProvider = ({ children }: { children: ReactNode }) => {
    const [errorInfo, setErrorInfo] = useState<ErrorInfo>({});

    // 在组件挂载时从cookie中读取服务器端设置的错误信息
    useEffect(() => {
        if (typeof window !== 'undefined') {
            // 读取cookie中的错误信息
            const errorCode = getCookie('error_code');
            const errorMessage = getCookie('error_message');
            const permissionCode = getCookie('permission_code');

            // 如果cookie中有错误信息，更新错误上下文状态
            if (errorCode || errorMessage || permissionCode) {
                setErrorInfo({
                    code: errorCode || undefined,
                    message: errorMessage || undefined,
                    permissionCode: permissionCode || undefined,
                });

                // 删除cookie中的错误信息，避免下次刷新页面时重复显示
                deleteCookie('error_code');
                deleteCookie('error_message');
                deleteCookie('permission_code');
            }
        }
    }, []);

    const clearErrorInfo = () => {
        setErrorInfo({});
    };

    return (
        <ErrorContext.Provider
            value={{ errorInfo, setErrorInfo, clearErrorInfo }}
        >
            {children}
        </ErrorContext.Provider>
    );
};
